import React, { FC, useCallback } from 'react'
import { GridDialogProps } from 'core/elements/grid/helpers/getGridDialogButton'
import Button from '../button'
import SimpleLink from 'core/components/SimpleLink'
import DialogButton from './DialogButton'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import ExternalLink from 'core/components/ExternalLink'
import { bottomMiddle } from 'core/elements/menu/defaults'
import clsx from 'clsx'

interface Action<T> {
  label: string
  icon?: string
  link?: string
  externalLink?: string
  DialogComponent?: FC<GridDialogProps<T>>
  disabledFn?: (item: T) => boolean
  tooltipFn?: (item: T) => string | React.ReactNode
  refreshFn?: () => void
  customDialogProps?: any
  onClick?: () => void
}

interface Actions<T> {
  leftActions?: Action<T>[]
  rightActions?: Action<T>[]
}

interface Props<T> {
  actions: Actions<T>
  entity: T
  className?: string
  isIconOnly?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  actionsGroup: {
    display: 'inline-grid',
    gap: 8,
    gridAutoFlow: 'column',
  },
}))

export default function OverviewActions<T>({
  actions,
  entity,
  className,
  isIconOnly = false,
}: Props<T>) {
  const { leftActions, rightActions } = actions

  const renderAction = useCallback(
    ({
      label,
      icon,
      link,
      externalLink,
      DialogComponent,
      disabledFn,
      hideFn,
      tooltipFn,
      refreshFn,
      customDialogProps,
      onClick,
    }) => {
      const disabled = disabledFn ? disabledFn(entity) : false
      const hidden = hideFn ? hideFn(entity) : false
      const tooltip = tooltipFn ? tooltipFn(entity) : ''
      if (hidden) {
        return null
      }
      return externalLink ? (
        <ExternalLink
          url={disabled ? '' : externalLink}
          key={label}
          data-testid={`external-link-${label}`}
        >
          <Button
            disabled={disabled}
            info={tooltip}
            infoPlacement={bottomMiddle.align}
            variant="secondary"
            icon={icon}
            data-testid={`button-${label}`}
          >
            {label}
          </Button>
        </ExternalLink>
      ) : link ? (
        <SimpleLink src={disabled ? '' : link} key={label} data-testid={`simple-link-${label}`}>
          <Button
            disabled={disabled}
            info={tooltip}
            infoPlacement={bottomMiddle.align}
            variant="secondary"
            icon={icon}
            data-testid={`button-${label}`}
          >
            {label}
          </Button>
        </SimpleLink>
      ) : DialogComponent ? (
        <DialogButton<T>
          key={label}
          label={label}
          icon={icon}
          disabled={disabled}
          DialogComponent={DialogComponent}
          refreshFn={refreshFn}
          customDialogProps={customDialogProps}
          entity={entity}
          info={tooltip}
          infoPlacement={bottomMiddle.align}
          data-testid={`dialog-button-${label}`}
        />
      ) : (
        <Button
          variant="secondary"
          key={label}
          icon={icon}
          onClick={onClick}
          info={tooltip}
          data-testid={`button-${label}`}
        >
          {!isIconOnly && label}
        </Button>
      )
    },
    [entity],
  )

  const classes = useStyles()
  return (
    <div className={classes.actions} data-testid="overview-actions-container">
      <div className={clsx(classes.actionsGroup, className)} data-testid="left-actions-group">
        {leftActions?.map(renderAction)}
      </div>
      <div className={clsx(classes.actionsGroup, className)} data-testid="right-actions-group">
        {rightActions?.map(renderAction)}
      </div>
    </div>
  )
}
